import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Save, Printer as PrinterIcon, Info, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../service/api';
import { useFirmSettingsStore } from '../store/firmSettingsStore';
import { useLabelStore } from '../store/labelStore';
import { usePrinterStore } from '../store/printerStore';
import { LabelCategory } from '../types/label';
import { IconTextButton } from '../components/ButtonComponents';
import { PROCESS_LIST } from './firm/label/PrintSettings';

interface AccountPrintSettingsProps {
  userId?: number; // Eğer verilirse admin tarafında spesifik bir kullanıcıyı yönettiğimizi belirtir
}

export const AccountPrintSettings: React.FC<AccountPrintSettingsProps> = ({ userId }) => {
  const { fetchSettings: fetchFirmSettings } = useFirmSettingsStore();
  const { data: labels, fetchDataDynamic: fetchLabels } = useLabelStore();
  const { data: printers, fetchDataDynamic: fetchPrinters } = usePrinterStore();

  const [userPrintSettings, setUserPrintSettings] = useState<any>({ routing: {} });
  const [firmPrintRouting, setFirmPrintRouting] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchLabels();
    fetchPrinters();
    loadUserSettings();
    loadFirmSettings();
  }, [userId]);

  const loadUserSettings = async () => {
    setIsLoading(true);
    try {
      const endpoint = userId ? `/user/${userId}/print-settings` : '/user/print-settings/my';
      const res = await api.get(endpoint);
      setUserPrintSettings(res.data || { routing: {} });
    } catch (e) {
      console.error("Yazdırma ayarları yüklenemedi", e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFirmSettings = async () => {
    const res = await fetchFirmSettings();
    if (res) {
      const appSettings = typeof res.firmAppSettings === 'string' ? JSON.parse(res.firmAppSettings) : (res.firmAppSettings || {});
      setFirmPrintRouting(appSettings.printSettings?.routing || {});
    }
  };

  const updateRouting = (category: string, printerId: any, templateId: any) => {
    setUserPrintSettings((prev: any) => ({
      ...prev,
      routing: {
        ...(prev.routing || {}),
        [category]: { printerId, templateId }
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const endpoint = userId ? `/user/${userId}/print-settings` : '/user/print-settings/my';
      await api.put(endpoint, { printSettings: userPrintSettings });
      toast.success(userId ? "Personel yazdırma ayarları güncellendi." : "Kişisel yazdırma ayarlarınız başarıyla kaydedildi.");
    } catch (e) {
      toast.error("Ayarlar kaydedilirken hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const printerOptions = [
    { label: 'Yazıcı Seçimi Yok', id: '' },
    { label: 'Varsayılan Sistem Yazıcısı', id: 'DEFAULT' },
    ...printers.map(p => ({ label: p.name || `Yazıcı ${p.id}`, id: p.id }))
  ];

  const templateOptions = [
    { name: 'Şablon Seçimi Yok', id: '' },
    ...labels
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 bg-card rounded-2xl border border-borderColor shadow-sm">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl shadow-lg border border-borderColor overflow-hidden mt-6 flex flex-col transition-all duration-300">
      
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-b border-borderColor/50 gap-4 bg-gradient-to-r from-bgAlt to-transparent">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary/10 rounded-xl">
            <PrinterIcon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text">
              {userId ? "Personel Yazdırma Yönlendirmeleri" : "Kişisel Yazdırma Yönlendirmeleri"}
            </h2>
            <p className="text-sm text-text/60 mt-1">
              {userId 
                ? "Personelin firmanın varsayılan ayarlarını ezeceği kişisel yönlendirmeler." 
                : "Firmanın varsayılan ayarlarını kendi cihazınız için ezmek (override) istiyorsanız kullanın."}
            </p>
          </div>
        </div>
        <IconTextButton 
          onClick={handleSave} 
          icon={Save} 
          className="btn-new shadow-lg shadow-primary/20 px-6 py-2.5 rounded-xl whitespace-nowrap"
          disabled={isSaving}
        >
          {isSaving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
        </IconTextButton>
      </div>

      <div className="p-4 bg-yellow-500/10 border-b border-yellow-500/20 text-yellow-700 dark:text-yellow-400 text-sm flex items-start gap-2">
        <Info className="w-5 h-5 shrink-0 mt-0.5" />
        <p>
          Boş bıraktığınız veya "Seçim Yok" dediğiniz durumlarda, o operasyon için 
          <span className="font-semibold mx-1">Firma Varsayılan Ayarı</span> 
          geçerli olacaktır. Personel bazlı kısıtlama yapmak isterseniz buradan seçim yapabilirsiniz.
        </p>
      </div>

      {/* Grid Content */}
      <div className="p-0">
        <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-bgAlt/50 text-xs font-bold text-text/70 uppercase tracking-wider">
          <div className="col-span-4 pl-4">Operasyon Süreci</div>
          <div className="col-span-4">Kişisel Yazıcı Seçimi</div>
          <div className="col-span-4 pr-4">Kişisel Şablon Seçimi</div>
        </div>
        
        <div className="flex flex-col divide-y divide-borderColor/50">
          {PROCESS_LIST.map((proc) => {
            const userRoute = userPrintSettings?.routing?.[proc.category] || { printerId: '', templateId: '' };
            const firmRoute = firmPrintRouting[proc.category] || { printerId: '', templateId: '' };
            
            // Bulabildiğimiz isimleri getirelim
            const firmPrinterName = printers.find(p => p.id === firmRoute.printerId)?.name || (firmRoute.printerId === 'DEFAULT' ? 'Varsayılan Sistem Yazıcısı' : 'Atanmamış');
            const firmTemplateName = labels.find(l => l.id === firmRoute.templateId)?.name || 'Atanmamış';

            return (
              <div 
                key={proc.category} 
                className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 md:p-6 items-center hover:bg-bgAlt/30 transition-colors group"
              >
                {/* Etiket */}
                <div className="col-span-1 md:col-span-4 md:pl-4">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary/40 group-hover:bg-primary transition-colors"></div>
                    <div>
                      <div className="font-semibold text-text">{proc.label}</div>
                      <div className="text-[10px] text-text/50 font-mono mt-0.5 bg-text/5 inline-block px-1.5 py-0.5 rounded">{proc.category}</div>
                    </div>
                  </div>
                </div>
                
                {/* Yazıcı */}
                <div className="col-span-1 md:col-span-4">
                  <label className="text-[11px] font-semibold text-text/50 uppercase tracking-wider mb-1.5 block md:hidden">Kişisel Yazıcı Seçimi</label>
                  <div className="relative">
                    <Dropdown 
                      value={userRoute.printerId || ''}
                      options={printerOptions}
                      optionLabel="label"
                      optionValue="id"
                      onChange={(e) => updateRouting(proc.category, e.value, userRoute.templateId)}
                      placeholder="(Varsayılan: Firma Ayarı)"
                      className="w-full text-sm !rounded-xl !border-borderColor focus:!border-primary/50 focus:!shadow-[0_0_0_2px_rgba(var(--primary),0.1)] transition-all"
                      filter
                      showClear
                    />
                    {!userRoute.printerId && (
                      <div className="absolute -bottom-5 left-1 text-[10px] text-text/50 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                        <span className="w-1 h-1 rounded-full bg-text/30"></span> Firma: {firmPrinterName}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Şablon */}
                <div className="col-span-1 md:col-span-4 md:pr-4 mt-4 md:mt-0">
                  <label className="text-[11px] font-semibold text-text/50 uppercase tracking-wider mb-1.5 block md:hidden">Kişisel Şablon Seçimi</label>
                  <div className="relative">
                    <Dropdown 
                      value={userRoute.templateId || ''}
                      options={templateOptions}
                      optionLabel="name"
                      optionValue="id"
                      onChange={(e) => updateRouting(proc.category, userRoute.printerId, e.value)}
                      placeholder="(Varsayılan: Firma Ayarı)"
                      className="w-full text-sm !rounded-xl !border-borderColor focus:!border-primary/50 focus:!shadow-[0_0_0_2px_rgba(var(--primary),0.1)] transition-all"
                      filter
                      showClear
                    />
                    {!userRoute.templateId && (
                      <div className="absolute -bottom-5 left-1 text-[10px] text-text/50 flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                         <span className="w-1 h-1 rounded-full bg-text/30"></span> Firma: {firmTemplateName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
